// An implicit forest (Tristan Hume, https://thume.ca/2021/03/14/iforests/): a range
// query over N values in O(log N) out of one flat array of 2N - 1 slots, with no
// pointers and no padding to a power of two.
//
// The values sit at the even slots. Each odd slot aggregates the complete subtree
// around it - the one whose leaves reach 2^(L-1) values to its left and to its right,
// where L is the number of trailing one bits of its index - so the array is a forest of
// perfect trees written in depth-first in-order:
//
//   slot   0   1   2   3   4   5   6   7   8 ...      3 covers values 0..3
//   holds v0  01  v1 0123 v2  23  v3 0..7 v4 ...      7 covers values 0..7
//
// A value's slot is fixed the moment it is written, so appending is a push and a fixup
// of the few nodes it completes (amortised O(1)), which is what a running simulation's
// trace would need. A query walks the leftmost bound rightwards, each step taking the
// widest node that starts there and does not overshoot the right bound.

// The level of node `i`: how many perfect trees end at it.
function trailingOnes(i) {
  let k = 0;
  while (i & 1) { k++; i >>>= 1; }
  return k;
}

export class IForest {
  // `combine` must be associative; `unit` is its identity, and `Arr` the typed array
  // the values (and so the aggregates) fit in.
  constructor(combine, unit, Arr = Float32Array, capacity = 0) {
    this.combine = combine;
    this.unit = unit;
    this.Arr = Arr;
    this.a = new Arr(capacity > 0 ? 2 * capacity - 1 : 0);
    this.n = 0;                              // values
    this.len = 0;                            // slots in use: 2n - 1
  }

  static max(values, n) { return fill(new IForest(maxOf, -Infinity, Float32Array, n), values, n); }
  static or(values, n) { return fill(new IForest(orOf, 0, Uint16Array, n), values, n); }
  static or32(values, n) { return fill(new IForest(orOf32, 0, Uint32Array, n), values, n); }
  // Only worth it over a prefix-sum array when values change or the type must stay
  // integral; a plain prefix sum answers a range sum in one subtraction.
  static sum(values, n) { return fill(new IForest(addOf, 0, Float64Array, n), values, n); }

  get bytes() { return this.a.byteLength; }

  push(v) {
    const i = 2 * this.n;
    if (i >= this.a.length) this.grow(this.n + 1);
    const a = this.a;
    a[i] = v;
    this.len = i + 1;
    this.n++;
    // The value at 2k completes the nodes at 2k+1-2^L for L = 1.. while the index has
    // exactly L trailing ones: as many as k has trailing one bits, two on average.
    for (let L = 1; ; L++) {
      const j = i + 1 - (1 << L);
      if (j < 0 || trailingOnes(j) !== L) break;
      const h = 1 << (L - 1);
      a[j] = this.combine(a[j - h], a[j + h]);
    }
    return this;
  }

  grow(want) {
    let cap = Math.max(4, (this.a.length + 1) >> 1);
    while (cap < want) cap *= 2;
    const b = new this.Arr(2 * cap - 1);
    b.set(this.a.subarray(0, this.len));
    this.a = b;
  }

  // Over the values [lo, hi). Every node it reads is complete, since hi <= n.
  query(lo, hi) {
    if (lo < 0) lo = 0;
    if (hi > this.n) hi = this.n;
    const a = this.a, combine = this.combine;
    let acc = this.unit;
    for (let p = lo; p < hi;) {
      // The widest perfect tree starting at value p that fits in [p, hi).
      let L = 0;
      while ((p & ((2 << L) - 1)) === 0 && p + (2 << L) <= hi) L++;
      acc = combine(acc, a[2 * p + (1 << L) - 1]);
      p += 1 << L;
    }
    return acc;
  }
}

function fill(f, values, n = values.length) {
  for (let i = 0; i < n; i++) f.push(values[i]);
  return f;
}

const maxOf = (a, b) => (a > b ? a : b);
const orOf = (a, b) => a | b;
const orOf32 = (a, b) => (a | b) >>> 0;
const addOf = (a, b) => a + b;
